import styled from "styled-components";
import React, {
  ChangeEvent,
  useState,
  DragEvent,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
} from "@mui/material";
import BuyerDetailsForm from "./BuyerDetailsForm";
import PickupAddressForm from "../orders/PickupAddressForm";
import OrderDetailsForm from "./OrderDetailsForm";
import PackageDetailsForm from "./PackageDetailsForm";
import { CustomTabPanel, Card, Title } from "../../ui/uiImport";
import { bulkOrderUpload, getOrderDetails } from "./orderApi";
import BreadCrumbs from "../../ui/BreadCrumbs";
import { getCustomerDetailsApi } from "../setUp/setupApi";
import { CustomerDetailsType } from "../setUp/CustomerDetails";
import { OrderCard } from "../../ui/Card";
import { StyledButton } from "../../ui/StyledButton";
import { IndvOrder } from "./OrderDetails";
import { api } from "../../../config/api";

const FileUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadArea = styled.div`
  width: 100%;
  margin: 40px 0 0 0;
  padding: 50px;
  border: 2px dashed #ccc;
  text-align: center;
  cursor: pointer;
  border-radius: 10px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

interface stepsType {
  label: string;
}

const steps: stepsType[] = [
  {
    label: "Buyer Details",
  },
  {
    label: "Pickup Details",
  },
  {
    label: "Order Details",
  },
  {
    label: "Package Details",
  },
];

const addOrderType = [
  {
    label: "SingleOrder",
  },
  {
    label: "BulkOrder",
  },
];

interface PackageDetailsFormProps {
  length: string | number;
  width: string | number;
  height: string | number;
  weight: string | number;
  description: string;
  quantity: string | number;
  product_name: string;
  [key: string]: string | number;
}

export interface OrderFormType {
  package_details: PackageDetailsFormProps[];
  items_identical: boolean;
  invoice_value: string;
  freight_on_carrier: string;
  invoice_number: string;
  ewaybill_number: string;
  order_id: string;
  is_return: boolean;
  cod: boolean;
  cod_amount: string;
  order_channel: string;
  billing_address:
    | string
    | {
        address: string;
        postal_code: string;
        city: string;
        state: string;
      };
  receiver_address: {
    address: string;
    postal_code: string;
    city: string;
    state: string;
  };
  receiver_contact: {
    person_name: string;
    company_name: string;
    mobile_number: string;
    email: string;
  };
  shipper_address: {
    id: null | string;
    nickname: string;
    address: string;
    postal_code: string;
    city: string;
    state: string;
  };
  shipper_contact: {
    person_name: string;
    company_name: string;
    mobile_number: string;
    email: string;
  };
  [key: string]:
    | {
        [key: string]: string | null;
      }
    | string
    | boolean
    | PackageDetailsFormProps[];
}

const AddOrder = () => {
  const matches = useMediaQuery("(min-width:600px)");

  const defaultForm: OrderFormType = {
    items_identical: false,
    order_channel: "custom",
    invoice_value: "",
    freight_on_carrier: "NI",
    invoice_number: "",
    ewaybill_number: "",
    order_id: "",
    is_return: false,
    cod: false,
    cod_amount: "",
    billing_address: "receiver_address",
    receiver_address: {
      address: "",
      postal_code: "",
      city: "",
      state: "",
    },
    receiver_contact: {
      person_name: "",
      company_name: "",
      mobile_number: "",
      email: "",
    },
    shipper_address: {
      id: null,
      nickname: "",
      address: "",
      postal_code: "",
      city: "",
      state: "",
    },
    shipper_contact: {
      person_name: "",
      company_name: "",
      mobile_number: "",
      email: "",
    },
    package_details: [
      {
        length: "",
        width: "",
        height: "",
        weight: "",
        description: "",
        quantity: 0,
        product_name: "",
      },
    ],
  };

  const [finalForm, setFinalForm] = useState<OrderFormType>(defaultForm);
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const mobileNumber = searchParams.get("customer") || "";
  const orderId = searchParams.get("order_id");
  const index = parseInt(searchParams.get("index") ?? "0") || 0;

  useEffect(() => {
    if (mobileNumber.length === 10) {
      getCustomerDetailsApi(mobileNumber)
        .then((data) => {
          const customerData = data?.data?.data as CustomerDetailsType;

          const form = { ...finalForm };

          form.receiver_address.address =
            customerData?.address_list[index]?.address;
          form.receiver_address.city = customerData?.address_list[index]?.city;
          form.receiver_address.postal_code =
            customerData?.address_list[index]?.postal_code;
          form.receiver_address.state =
            customerData?.address_list[index]?.state;

          form.receiver_contact.mobile_number = mobileNumber;
          form.receiver_contact.email =
            customerData?.address_list[index]?.email;
          form.receiver_contact.person_name =
            customerData?.address_list[index]?.person_name;

          setFinalForm(form);
        })
        .catch((error) => console.log(error));
    }
  }, []);

  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (orderId) {
      api
        .get("/b2c/edit_shipment_details/", {
          params: { id: orderId },
        })
        .then((data) => {
          const orderData = data.data?.data as OrderFormType;
          let finalEditForm = { ...finalForm, ...orderData };
          setFinalForm(finalEditForm);
          setIsEdit(true);
        })
        .catch((err) => {
          navigate("/order");
        });
    }
  }, []);

  const [activeStep, setActiveStep] = useState<number>(0);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const navigate = useNavigate();

  const [csvError, setCsvError] = useState(false);
  const [csvErrorData, setCsvErrorData] = useState<any>();

  const [csvHeaderError, setCsvHeaderError] = useState(false);
  const [csvHeaderErrorData, setCsvHeaderErrorData] = useState<any>();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
    setCsvError(false);
    setCsvHeaderError(false);
    setCsvErrorData(null);
    setCsvHeaderErrorData(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    setSelectedFile(file || null);
    setCsvError(false);
    setCsvHeaderError(false);
    setCsvErrorData(null);
    setCsvHeaderErrorData(null);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleUpload = useCallback(() => {
    if (selectedFile) {
      bulkOrderUpload(selectedFile)
        .then((data) => {
          setSelectedFile(null);
          navigate("/order");
        })
        .catch((error) => {
          let csvData = error?.response?.data?.data;

          if (Array.isArray(csvData)) {
            setCsvError(true);
            setCsvErrorData(csvData);
          } else if (typeof csvData === "object" && csvData !== null) {
            setCsvHeaderError(true);
            setCsvHeaderErrorData(csvData);
          }
        });
    }
  }, [selectedFile, navigate]);

  useEffect(() => {
    handleUpload();
  }, [handleUpload]);

  const downdloadCsvFromApi = async () => {
    const response = await api.get("/b2c/download/csv/");
    window.open(response?.data?.data, "_blank");
  };

  return (
    <>
      <BreadCrumbs
        field="Order"
        link={location.state?.prevurl || "/order"}
        extra={[
          {
            text: "Add Order",
          },
        ]}
      />

      <Grid container rowGap={"20px"} columnGap={"20px"}>
        <Grid item md={12}>
          {csvError && (
            <OrderCard>
              <TableContainer
                sx={{
                  marginTop: "20px",
                }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Row</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvErrorData.map((key1: any, index1: number) =>
                      Object.keys(key1).map(
                        (key2: any, index2: number) =>
                          key2 !== "line_num" && (
                            <TableRow>
                              <TableCell>
                                <Typography fontSize={"14px"} color={"red"}>
                                  {key2}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography fontSize={"14px"} color={"red"}>
                                  {key1[key2]}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography fontSize={"14px"} color={"red"}>
                                  {key1.line_num}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </OrderCard>
          )}
          {csvHeaderError && (
            <OrderCard>
              <TableContainer
                sx={{
                  marginTop: "20px",
                }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Header</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(csvHeaderErrorData).map(
                      (key2: any, index2: number) => (
                        <TableRow>
                          <TableCell>
                            <Typography fontSize={"14px"} color={"red"}>
                              {key2}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={"14px"} color={"red"}>
                              {csvHeaderErrorData[key2]}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </OrderCard>
          )}
        </Grid>
        <Grid item md={7}>
          <Card>
            {activeStep === 0 && (
              <BuyerDetailsForm
                handleNext={handleNext}
                label={steps[activeStep].label}
                setFinalForm={setFinalForm}
                finalForm={finalForm}
              />
            )}
            {activeStep === 1 && (
              <PickupAddressForm
                handleNext={handleNext}
                handleBack={handleBack}
                label={steps[activeStep].label}
                setFinalForm={setFinalForm}
                finalForm={finalForm}
              />
            )}
            {activeStep === 2 && (
              <OrderDetailsForm
                handleNext={handleNext}
                handleBack={handleBack}
                label={steps[activeStep].label}
                setFinalForm={setFinalForm}
                finalForm={finalForm}
              />
            )}
            {activeStep === 3 && (
              <PackageDetailsForm
                handleBack={handleBack}
                label={steps[activeStep].label}
                setFinalForm={setFinalForm}
                finalForm={finalForm}
                isEdit={isEdit}
              />
            )}
          </Card>
        </Grid>

        <Grid item md={4.8}>
          <OrderCard>
            {/* <CustomTabPanel index={1} value={activeTab}> */}

            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Title title="Bulk Orders" />
              <StyledButton onClick={downdloadCsvFromApi} variant="contained">
                <Typography fontSize={10}>Download Template</Typography>
              </StyledButton>
            </Stack>
            <Box>
              <Box>
                <Typography fontSize={14} fontWeight={600} marginBottom={1}>
                  Import Bulk Orders
                </Typography>
                <Typography fontSize={12}>
                  Download the sample file and replace its data with your order
                  data. Make sure all mandatory fields are filled. <br /> Save
                  the file and upload it back.
                </Typography>
              </Box>

              <Box>
                <FileUploadWrapper>
                  <FileInput
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <UploadArea
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    {selectedFile ? (
                      <div>
                        <Typography>
                          Selected File: {selectedFile?.name}
                        </Typography>
                        <Typography>
                          Size: {selectedFile?.size} bytes
                        </Typography>
                      </div>
                    ) : (
                      <>
                        <Typography>
                          Drag And Drop to upload the files here.
                        </Typography>
                        <Typography>OR</Typography>
                      </>
                    )}
                    <StyledButton variant="contained">
                      <Typography fontSize={12}>Browse and Upload</Typography>
                    </StyledButton>
                  </UploadArea>
                </FileUploadWrapper>
              </Box>
            </Box>
          </OrderCard>

          {/* </CustomTabPanel> */}
        </Grid>
      </Grid>
    </>
  );
};

export default AddOrder;
